<template>
  <div class="padding-y-24 padding-x-16">
    <Dropdown
      v-if="item.advanced"
      :value="form.actionType"
      :options="dropdownOptions"
      name="actionType"
      id="actionType"
      inputPlaceholder=""
      rowClass="margin-bottom-16"
      :hasRemoveIcon="false"
      @input="input"
      @change="changeActionType"
    />
    <DatePicker
      v-if="!isBetween"
      :placeholder="persianType('current')"
      :value="form.date"
      name="date"
      id="date"
      :rightIcon="persianType('current', 'icons')"
      :displayFormat="persianType('current', 'displayFormats')"
      :format="persianType('current', 'formats')"
      :type="item.type"
      :popover="true"
      :simple="true"
      :autoSubmit="true"
      :hasEraseIcon="true"
      :vuelidateObj="$v.form.date"
      @input="inputDatePicker"
      @focus="resetValidation"
      @blur="checkValidation"
      @erase="eraseDatePicker"
    />
    <span v-if="isBetween">
      <DatePicker
        :placeholder="persianType('start')"
        :value="form.startAt"
        name="startAt"
        id="startAt"
        :rightIcon="persianType('current', 'icons')"
        :displayFormat="persianType('start', 'displayFormats')"
        :format="persianType('start', 'formats')"
        :type="item.type"
        :popover="true"
        :simple="true"
        :autoSubmit="true"
        :hasEraseIcon="true"
        :vuelidateObj="$v.form.startAt"
        @input="inputDatePicker"
        @focus="resetValidation"
        @blur="checkValidation"
        @erase="eraseDatePicker"
      />
      <DatePicker
        :placeholder="persianType('end')"
        :value="form.endAt"
        name="endAt"
        id="endAt"
        :rightIcon="persianType('current', 'icons')"
        :displayFormat="persianType('end', 'displayFormats')"
        :format="persianType('end', 'formats')"
        :type="item.type"
        :popover="true"
        :simple="true"
        :autoSubmit="true"
        :hasEraseIcon="true"
        :disabled="!form.startAt"
        :vuelidateObj="$v.form.endAt"
        @input="inputDatePicker"
        @focus="resetValidation"
        @blur="checkValidation"
        @erase="eraseDatePicker"
      />
    </span>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import { Dropdown, DatePicker } from "@/components/re-design/forms";
import { required } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";

export default {
  mixins: [inputMixin],
  components: { Dropdown, DatePicker },
  props: {
    item: {
      type: Object,
      default: null,
    },
    selectedFilter: {
      type: [Object, Array],
      default: null,
    },
  },
  validations() {
    return {
      form: {
        actionType: {},
        date: {},
        startAt: { required },
        endAt: {},
      },
    };
  },
  data() {
    return {
      form: {
        actionType: null,
        date: null,
        startAt: null,
        endAt: null,
      },
    };
  },
  computed: {
    isBetween() {
      return pathOr(null, ["id"], this.form.actionType) === "between";
    },
    dropdownOptions() {
      const types = {
        datetime: this.$lang.datetime,
        date: this.$lang.date,
      };
      const word = pathOr(this.$lang.date, [this.item.type], types);
      return [
        {
          id: "eq",
          caption: `${word} ${this.$lang.current}`,
        },

        {
          id: "gt",
          caption: `${this.$lang.after} ${word}`,
        },
        {
          id: "lt",
          caption: `${this.$lang.before} ${word}`,
        },
        {
          id: "gte",
          caption: `${word} ${this.$lang.current_and_after}`,
        },
        {
          id: "lte",
          caption: `${word} ${this.$lang.current_and_before}`,
        },
        {
          id: "between",
          caption: `${this.$lang.area} ${word}ی`,
        },
      ];
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(item) {
        // set default item of actionType dropdown
        const types = {
          datetime: this.$lang.datetime,
          date: this.$lang.date,
        };
        const word = pathOr(this.$lang.datetime, [item.type], types);

        this.form.actionType = {
          id: "eq",
          caption: `${word} ${this.$lang.current}`,
        };
      },
    },
    selectedFilter: {
      immediate: true,
      handler(data) {
        if (data) {
          this.generateSelectedFilter(data);
        }
      },
    },
  },
  methods: {
    pathOr,
    includes,
    persianType(key, objKey = "langs") {
      const type = pathOr(null, ["type"], this.item);
      const keyType = objKey === "langs" ? `${key}_${type}` : type;
      const obj = {
        icons: {
          datetime: "calendar-clock",
          date: "calendar-blank",
        },
        displayFormats: {
          datetime: `jYYYY/jMM/jDD ${this.$lang.hours} HH:mm`,
          date: "jYYYY/jMM/jDD",
        },
        formats: {
          datetime: "YYYY/MM/DD HH:mm",
          date: "YYYY/MM/DD",
        },
        langs: {
          current_datetime: this.$lang.datetime,
          current_date: this.$lang.date,
          start_datetime: this.$lang.start_datetime,
          end_datetime: this.$lang.end_datetime,
          start_date: this.$lang.start_date,
          end_date: this.$lang.end_date,
        },
      };
      return pathOr("", [keyType], obj[objKey]);
    },
    resetAllCalender() {
      Object.keys(this.form).forEach((key) => {
        if (key !== "actionType") {
          this.form[key] = null;
        }
      });
      this.$v.$reset();
    },
    changeActionType() {
      this.resetAllCalender();
      this.generateServiceData();
    },
    inputDatePicker(key, value) {
      this.input(key, value);
      this.generateServiceData();
    },
    eraseDatePicker(key) {
      this.input(key, null);
      if (key === "startAt") {
        this.input("endAt", null);
      }
      this.generateServiceData();
    },
    generateSelectedFilter(data) {
      const getOfFilterCache = Array.isArray(data);
      if (getOfFilterCache) {
        const actionTypeId = pathOr("eq", [0], data);
        this.form.actionType = this.dropdownOptions.find(
          (option) => option.id === actionTypeId
        );
        if (actionTypeId === "between") {
          this.form.date = null;
          this.form.startAt = pathOr(null, [1], data);
          this.form.endAt = pathOr(null, [2], data);
        } else {
          this.form.date = pathOr(null, [1], data);
          this.form.startAt = null;
          this.form.endAt = null;
        }
      } else {
        for (const key in data) {
          if (Object.hasOwnProperty.call(data, key)) {
            const value = data[key];
            this.input(key, value);
          }
        }
      }
    },
    generateServiceData() {
      this.$emit("getFilterData", this.item, this.form);
    },
  },
};
</script>
